/**
 * Global Constants
 * Centralizes configuration values, magic strings, and default terminology.
 */

import { Capability } from './types';


/**
 * Path prefixes that are always public platform (marketing/tools) pages,
 * regardless of whether an active_org is stored in localStorage.
 * Used by TenantContext to prevent org-node names from polluting SEO titles.
 */
export const PUBLIC_PLATFORM_PATHS = [
  '/',
  '/showcase',
  '/features',
  '/plans',
  '/board-memo',
  '/board-pitch',
  '/use-cases',
  '/about',
  '/mission',
  '/newsroom',
  '/contact',
  '/privacy',
  '/terms',
  '/cookies',
  '/security',
  '/dpa',
  '/aup',
  '/dmca',
  '/tools',
  '/sign-in',
  '/sign-up',
  '/demo',
  '/join',
];

export const DEFAULT_ENABLED_MODULES = [
  'PULSE',
  'DISCUSSIONS',
  'CALENDAR',
  'SUPPORT',
  'DIRECTORY',
] as const;

export const TERMINOLOGY_DEFAULTS: Record<string, string> = {
  member: 'Member',
  childNode: 'Chapter',
  parentNode: 'Council',
  pulse: 'Pulse',
  ledger: 'Ledger',
  requirements: 'Requirements',
  goals: 'Goals',
  discussions: 'Message Board',
  support: 'Help Desk'
};

export const ROLE_PRIORITY: Record<string, number> = {
  // Global System Roles
  'SUPER_ADMIN': 250,
  'SUPER_USER': 250,
  'SUPERUSER': 250,
  'SYSTEM_ADMIN': 200,
  'OPERATIONS_ADMIN': 190,
  'SUPPORT_ADMIN': 180,
  'SUPPORT': 150,

  // Level 100: Top Level Organization Owners
  'OWNER': 100,
  'FOUNDER': 100,

  // Level 95: Primary Administrators
  'ADMIN': 95,
  'MANAGER': 95,
  'DIRECTOR': 95,
  'PRESIDENT': 95,
  
  // Level 85: Secondary Officers
  'VICE_PRESIDENT': 85,
  'TREASURER': 85,
  'SECRETARY': 85,

  // Level 80: Team/Shift Leaders
  'SUPERVISOR': 80,
  'CAPTAIN': 80,
  'LEAD': 80,

  // Level 70: Coordinators
  'MODERATOR': 70,
  'COORDINATOR': 70,

  // Level 50: Standard Members
  'MEMBER': 50,
  'EMPLOYEE': 50,
  'STAFF': 50,

  // Level 40: Restricted / Junior Members
  'DEPENDENT': 40,
  'STUDENT': 40,
  'CHILD': 40,

  // Level 0: Unauthenticated / Outsiders
  'GUEST': 0,
  'PROSPECT': 0
};

export const ALL_CAPABILITIES: Capability[] = [
  'create_announcement', 'send_email_blast', 'manage_members', 'view_ledger',
  'configure_fees', 'verify_requirement', 'create_event', 'create_poll',
  'manage_vault', 'view_analytics', 'edit_settings', 'create_newsletter',
  'manage_message_board', 'create_goal', 'create_requirement', 'manage_referrals',
  'manage_elections', 'cast_vote', 'view_suborgs', 'create_sub_orgs',
  'view_newsletter', 'view_chat', 'view_calendar', 'view_requirements', 
  'view_goals', 'view_discussions', 'view_polls', 'view_directory', 
  'view_elections', 'view_leadership', 'view_ledger_admin', 'view_personal_finances',
  'view_support', 'manage_support', 'SUPPORT_VIEW', 'SUPPORT_MANAGE',
  'ROLE_MANAGE', 'MEMBER_MANAGE', 'MEMBER_INVITE', 'VAULT_UPLOAD', 'VAULT_VIEW',
  'VAULT_MANAGE', 'CONFIG_UPDATE', 'COMMUNICATION_MANAGE', 'GOAL_MANAGE',
  'GOAL_UPDATE', 'EVENT_MANAGE', 'REQUIREMENT_MANAGE', 'EVIDENCE_VERIFY'
];

export const CAPABILITY_ALIASES: Record<string, string[]> = {
  // Roles & Members
  'ROLE_MANAGE': ['ROLE_MANAGE', 'define_roles', 'manage_members'],
  'define_roles': ['ROLE_MANAGE', 'define_roles', 'manage_members'],
  'MEMBER_MANAGE': ['MEMBER_MANAGE', 'manage_members'],
  'manage_members': ['MEMBER_MANAGE', 'manage_members', 'ROLE_MANAGE', 'MEMBER_INVITE'],
  'MEMBER_INVITE': ['MEMBER_INVITE', 'manage_members'],

  // Vault & Storage
  'VAULT_UPLOAD': ['VAULT_UPLOAD', 'manage_vault'],
  'VAULT_VIEW': ['VAULT_VIEW', 'manage_vault', 'view_vault'],
  'VAULT_MANAGE': ['VAULT_MANAGE', 'manage_vault'],
  'manage_vault': ['VAULT_MANAGE', 'VAULT_UPLOAD', 'VAULT_VIEW', 'manage_vault'],

  // Stripe & Configuration
  'CONFIG_UPDATE': ['CONFIG_UPDATE', 'edit_settings', 'configure_fees'],
  'edit_settings': ['CONFIG_UPDATE', 'edit_settings'],
  'configure_fees': ['CONFIG_UPDATE', 'configure_fees', 'edit_settings'],

  // Communications / Newsletters
  'COMMUNICATION_MANAGE': ['COMMUNICATION_MANAGE', 'create_newsletter', 'edit_newsletter', 'send_email_blast'],
  'create_newsletter': ['COMMUNICATION_MANAGE', 'create_newsletter', 'edit_newsletter'],
  'edit_newsletter': ['COMMUNICATION_MANAGE', 'edit_newsletter', 'create_newsletter'],
  'send_email_blast': ['COMMUNICATION_MANAGE', 'send_email_blast'],

  // Goals & Targets
  'GOAL_MANAGE': ['GOAL_MANAGE', 'GOAL_UPDATE', 'create_goal'],
  'GOAL_UPDATE': ['GOAL_UPDATE', 'GOAL_MANAGE', 'create_goal'],
  'create_goal': ['GOAL_MANAGE', 'GOAL_UPDATE', 'create_goal'],

  // Support Tickets
  // Manage implies view: a manager with no view would get an unusable queue.
  'view_support': ['view_support', 'SUPPORT_VIEW', 'manage_support', 'SUPPORT_MANAGE'],
  'SUPPORT_VIEW': ['view_support', 'SUPPORT_VIEW', 'manage_support', 'SUPPORT_MANAGE'],
  'manage_support': ['manage_support', 'SUPPORT_MANAGE'],
  'SUPPORT_MANAGE': ['manage_support', 'SUPPORT_MANAGE'],

  // Events
  'EVENT_MANAGE': ['EVENT_MANAGE', 'create_event'],
  'create_event': ['EVENT_MANAGE', 'create_event'],

  // Requirements & Evidence
  'REQUIREMENT_MANAGE': ['REQUIREMENT_MANAGE', 'create_requirement'],
  'create_requirement': ['REQUIREMENT_MANAGE', 'create_requirement'],
  'EVIDENCE_VERIFY': ['EVIDENCE_VERIFY', 'verify_requirement'],
  'verify_requirement': ['EVIDENCE_VERIFY', 'verify_requirement']
};

export const TIER_LIMITS = {
  LOCAL: {
    storageBytes: 100 * 1024 * 1024, // 100MB
    maxMembers: 50,
    monthlyEmails: 100,
    monthlySms: 0,
    maxDepth: 1,
    platformFee: 0.50
  },
  FOUNDATION: {
    storageBytes: 1 * 1024 * 1024 * 1024, // 1GB
    maxMembers: 250,
    monthlyEmails: 1000,
    monthlySms: 0,
    maxDepth: 1,
    platformFee: 0.40
  },
  MOMENTUM: {
    storageBytes: 5 * 1024 * 1024 * 1024, // 5GB
    maxMembers: 1000,
    monthlyEmails: 10000,
    monthlySms: 200,
    maxDepth: 2,
    platformFee: 0.30
  },
  INSTITUTIONAL: {
    storageBytes: 25 * 1024 * 1024 * 1024, // 25GB
    maxMembers: 5000,
    monthlyEmails: 50000,
    monthlySms: 1000,
    maxDepth: 3,
    platformFee: 0.20
  }
};

/**
 * Capabilities that are gated by an organization's plan rather than by a role.
 *
 * This is the single source of truth. The nav (to show the lock), the route
 * guard (to actually block the URL) and the page (for defence in depth) all read
 * from here, so they can never disagree about who is allowed in — which they
 * previously did, three different ways.
 */
export type TierGatedFeature = 'chat' | 'subOrgs' | 'newsletterStudio';

export const TIER_GATED_FEATURES: Record<TierGatedFeature, readonly string[]> = {
  /** Real-time chat is exclusive to Momentum and Institutional. */
  chat: ['MOMENTUM', 'INSTITUTIONAL'],
  /** Sub-organizations require a network-capable plan. */
  subOrgs: ['MOMENTUM', 'INSTITUTIONAL'],
  /** The Newsletter Studio is available on every paid plan (not the free tier). */
  newsletterStudio: ['FOUNDATION', 'MOMENTUM', 'INSTITUTIONAL'],
};

/** True when `tier` satisfies the plans required for `feature`. Platform staff bypass. */
export const hasTierAccess = (
  feature: TierGatedFeature,
  tier: string | null | undefined,
  opts?: { isSystemAdmin?: boolean; isSuperAdmin?: boolean }
): boolean => {
  if (opts?.isSystemAdmin || opts?.isSuperAdmin) return true;
  if (!tier) return false;
  return TIER_GATED_FEATURES[feature].includes(tier);
};
